import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  CheckCircle, WarningCircle, Package, Flask, FileText, ArrowsClockwise, Copy, ChartBar,
  PaperPlaneTilt,
} from '@phosphor-icons/react';
import { PageHeader } from '@/components/enterprise/PageHeader';
import { Button } from '@/components/ui/button';
import { StatusBadge } from '@/components/enterprise/StatusBadge';
import { EmptyState } from '@/components/enterprise/EmptyState';
import { devApi, STAGE_TONE } from './devApi';
import { jstDateTime } from '@/utils/jstDate';

const STATE = {
  ready: { label: '試作できる', tone: 'success', icon: CheckCircle },
  waiting_parts: { label: '部品待ち', tone: 'warning', icon: Package },
  validation: { label: '検証中', tone: 'info', icon: Flask },
  missing_documents: { label: '書類が未登録', tone: 'warning', icon: FileText },
  not_ready: { label: '試作できない', tone: 'danger', icon: WarningCircle },
};

const Tile = ({ label, value, tone, testid }) => (
  <div className="rounded-lg border border-zinc-300 bg-white px-4 py-3 shadow-sm" data-testid={testid}>
    <p className="text-xs font-semibold text-zinc-600">{label}</p>
    <p className={`text-2xl font-bold tabular-nums ${tone}`}>{value}</p>
  </div>
);

/** 週次ダイジェスト — そのまま会議やチャットに貼れる日本語サマリー。 */
const DigestCard = () => {
  const [d, setD] = useState(null);
  const [open, setOpen] = useState(false);
  useEffect(() => { devApi.digest().then(setD).catch(() => {}); }, []);
  if (!d) return null;
  return (
    <div className="rounded-lg border border-zinc-300 bg-white shadow-sm" data-testid="dev-digest">
      <div className="flex flex-wrap items-center gap-2 border-b border-zinc-200 bg-zinc-100 px-4 py-2">
        <p className="text-sm font-bold text-zinc-800">週次ダイジェスト</p>
        <p className="text-xs text-zinc-600">
          試作できる {d.counts.ready} / 止まっている {d.counts.total - d.counts.ready} / 今月完了 {d.counts.completed_this_month}
        </p>
        <div className="ml-auto flex gap-2">
          <Button variant="outline" size="sm" data-testid="dev-digest-send"
            onClick={async () => {
              try {
                const r = await devApi.sendDigest();
                toast.success(`ダイジェストを${r.sent}名に送信しました（チャット）`);
              } catch (e) {
                toast.error(e.response?.data?.detail || '送信できませんでした');
              }
            }}>
            <PaperPlaneTilt size={14} className="mr-1" /> 今すぐ送信
          </Button>
          <Button variant="outline" size="sm" data-testid="dev-digest-repair"
            title="過去の自動ダイジェストが同僚の名前で届いていた場合、それを削除して Sensbey Bot 名義で再送します（管理者のみ）"
            onClick={async () => {
              if (!window.confirm('同僚の名義で届いた自動ダイジェストDMを削除し、Sensbey Bot 名義で再送します。よろしいですか？')) return;
              try {
                const r = await devApi.repairDigestAttribution();
                toast.success(`${r.removed}件を削除し、${r.resent_to}名に再送しました`
                  + (r.senders?.length ? `（対象の名義: ${r.senders.join('、')}）` : ''));
              } catch (e) {
                toast.error(e.response?.data?.detail || '修復できませんでした');
              }
            }}>
            送信者名を修復
          </Button>
          <Button variant="outline" size="sm" onClick={() => setOpen(!open)}
            data-testid="dev-digest-toggle">{open ? '閉じる' : '内容を見る'}</Button>
          <Button size="sm" data-testid="dev-digest-copy"
            onClick={async () => {
              try {
                await navigator.clipboard.writeText(d.text);
                toast.success('ダイジェストをコピーしました');
              } catch (_e) {
                setOpen(true);
                toast.error('コピーできませんでした。内容を選択してコピーしてください。');
              }
            }}>
            <Copy size={14} className="mr-1" /> コピー
          </Button>
        </div>
      </div>
      {open && (
        <pre className="whitespace-pre-wrap px-4 py-3 text-sm text-zinc-800"
          data-testid="dev-digest-text">{d.text}</pre>
      )}
    </div>
  );
};

/** 試作の準備状況 — 全案件を1ページで。管理者・マネージャー向け。 */
export default function ReadinessSnapshot() {
  const nav = useNavigate();
  const [data, setData] = useState(null);

  useEffect(() => {
    devApi.readiness().then(setData)
      .catch(() => toast.error('準備状況を読み込めませんでした'));
  }, []);

  if (!data) {
    return <div className="p-6 text-sm text-zinc-600" data-testid="dev-readiness-loading">読み込み中…</div>;
  }
  const c = data.counts;

  return (
    <div className="space-y-6" data-testid="dev-readiness-page">
      <PageHeader
        title="試作の準備状況"
        description="いま試作できる案件と、止まっている理由を全案件まとめて確認します。"
        testid="dev-readiness-header"
        secondary={
          <>
            <Button variant="outline" onClick={() => nav('/engineering/developments/insights')}
              data-testid="dev-readiness-insights-btn">
              <ChartBar size={15} className="mr-1" /> トラブル分析
            </Button>
            <Button variant="outline" onClick={() => nav('/engineering')}
              data-testid="dev-readiness-dashboard-btn">
              <ArrowsClockwise size={15} className="mr-1" /> ダッシュボード
            </Button>
          </>
        }
      />

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-5">
        <Tile label="試作できる" value={c.ready} tone="text-emerald-700" testid="dev-readiness-tile-ready" />
        <Tile label="部品待ち" value={c.waiting_parts} tone="text-amber-700" testid="dev-readiness-tile-parts" />
        <Tile label="検証中" value={c.validation} tone="text-blue-700" testid="dev-readiness-tile-validation" />
        <Tile label="書類が未登録" value={c.missing_documents} tone="text-amber-700"
          testid="dev-readiness-tile-docs" />
        <Tile label="試作できない" value={c.not_ready} tone="text-rose-700" testid="dev-readiness-tile-notready" />
      </div>

      {(data?.rows || []).length > 0 && <DigestCard />}

      {data.rows.length === 0 ? (
        <EmptyState icon={CheckCircle} title="進行中の開発案件はありません"
          testid="dev-readiness-empty" />
      ) : (
        <div className="overflow-x-auto rounded-lg border border-zinc-300 bg-white shadow-sm">
          <table className="w-full">
            <thead className="border-b border-zinc-300 bg-zinc-100">
              <tr className="text-left">
                <th className="px-3 py-2 text-xs font-semibold text-zinc-600">判定</th>
                <th className="px-3 py-2 text-xs font-semibold text-zinc-600">案件</th>
                <th className="px-3 py-2 text-xs font-semibold text-zinc-600">ステージ</th>
                <th className="px-3 py-2 text-xs font-semibold text-zinc-600">試作</th>
                <th className="px-3 py-2 text-xs font-semibold text-zinc-600">止まっている理由</th>
                <th className="px-3 py-2 text-xs font-semibold text-zinc-600">次にやること</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-200">
              {data.rows.map((r) => {
                const st = STATE[r.state] || STATE.not_ready;
                return (
                  <tr key={r.id} className="cursor-pointer hover:bg-teal-50/50"
                    onClick={() => nav(`/engineering/developments/${r.id}`)}
                    data-testid={`dev-readiness-row-${r.id}`}>
                    <td className="px-3 py-2">
                      <StatusBadge tone={st.tone} testid={`dev-readiness-state-${r.id}`}>
                        <st.icon size={12} weight="fill" /> {st.label}
                      </StatusBadge>
                    </td>
                    <td className="px-3 py-2">
                      <p className="text-sm font-semibold text-zinc-900">{r.title}</p>
                      <p className="text-xs text-zinc-600">
                        {r.development_type_label} · {r.project_name || 'プロジェクト未設定'}
                        {r.target_completion_date ? ` · 目標 ${r.target_completion_date}` : ''}
                      </p>
                    </td>
                    <td className="px-3 py-2">
                      <StatusBadge tone={STAGE_TONE[r.current_stage] || 'neutral'}>
                        {r.current_stage_label}
                      </StatusBadge>
                    </td>
                    <td className="px-3 py-2 text-xs text-zinc-700">
                      {r.prototypes ? `${r.prototype_name || ''} ${r.prototype_status_label || ''}` : '—'}
                    </td>
                    <td className="px-3 py-2 text-xs text-zinc-800">
                      {(r.blockers || []).length === 0 ? '—' : (
                        <ul className="list-disc pl-4">
                          {r.blockers.map((b, i) => <li key={i}>{b}</li>)}
                        </ul>
                      )}
                    </td>
                    <td className="px-3 py-2 text-xs font-semibold text-zinc-800">{r.next_action}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      <p className="text-xs text-zinc-500">作成: {jstDateTime(data.generated_at)}</p>
    </div>
  );
}
